import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { callbackUrlWithNext, sanitizeNextPath } from './next';

import type { Session, User } from '@supabase/supabase-js';

import { profileQueries, type Profile } from '@/lib/queries/profile';
import { supabase } from '@/lib/supabase';

export interface AuthContextValue {
  user: User | null;
  profile: Profile | null;
  session: Session | null;
  loading: boolean;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signInWithMagicLink: (email: string, next?: string) => Promise<void>;
  signInWithGoogle: (next?: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    metadata?: { username?: string; display_name?: string; invite_code?: string },
    next?: string,
  ) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const queryClient = useQueryClient();

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session ?? null);
      setSessionLoading(false);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      // Invalidate profile-bound queries so consumers refetch under the new identity.
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['creator-application'] });
      queryClient.invalidateQueries({ queryKey: ['subscriber-preferences'] });
    });

    return () => {
      mounted = false;
      subscription.subscription.unsubscribe();
    };
  }, [queryClient]);

  const userId = session?.user.id;
  const profileQuery = useQuery(profileQueries.byId(userId));

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  }, []);

  const signInWithMagicLink = useCallback(async (email: string, next?: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: callbackUrlWithNext(window.location.origin, next),
      },
    });
    if (error) throw error;
  }, []);

  const signInWithGoogle = useCallback(async (next?: string) => {
    // Respect a configured OAuth callback base if present; carry a sanitized
    // next as a query param either way.
    const base =
      import.meta.env.PUBLIC_GOOGLE_OAUTH_REDIRECT_URL ?? `${window.location.origin}/auth/callback`;
    const safeNext = sanitizeNextPath(next);
    const redirectTo =
      safeNext === '/'
        ? base
        : `${base}${base.includes('?') ? '&' : '?'}next=${encodeURIComponent(safeNext)}`;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo },
    });
    if (error) throw error;
  }, []);

  const signUp = useCallback(
    async (
      email: string,
      password: string,
      metadata?: { username?: string; display_name?: string },
      next?: string,
    ) => {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: callbackUrlWithNext(window.location.origin, next),
          data: metadata ?? {},
        },
      });
      if (error) throw error;
    },
    [],
  );

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    queryClient.clear();
  }, [queryClient]);

  const refreshProfile = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['profile', userId] });
  }, [queryClient, userId]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: session?.user ?? null,
      profile: profileQuery.data ?? null,
      session,
      loading: sessionLoading || profileQuery.isLoading,
      signInWithEmail,
      signInWithMagicLink,
      signInWithGoogle,
      signUp,
      signOut,
      refreshProfile,
    }),
    [
      session,
      profileQuery.data,
      profileQuery.isLoading,
      sessionLoading,
      signInWithEmail,
      signInWithMagicLink,
      signInWithGoogle,
      signUp,
      signOut,
      refreshProfile,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
